import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor, GTExpander, GTFrameworkCell } from 'src/components/GenericTable';
import { filter } from 'lodash';
import _ from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { formatNumber } from 'src/utils/misc';
import { Link, useNavigate } from 'react-router-dom';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { TextOverflowTooltip } from 'src/common';
import { ControlGql } from './types';
import { getMaturityLevelName } from 'src/api/types';

type useColumns = (a: { data?: ControlGql[] }) => {
  columns: ColumnDef<ControlGql>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['strategy', 'name', 'owner', 'framework_controls', 'status'];

export const useColumns: useColumns = ({ data = [] }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.rmpMultiSelectModal}-columns`,
    defaultColumns,
  );
  const navigate = useNavigate();

  const columns = useMemo(() => {
    let internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

    const list: ColumnDef<ControlGql>[] = [
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.strategy || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name || null,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/risk-management/control/${info.row.original.id}`}
              target="_blank"
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: { value: ['Modal:UpdateControls'] },
                  destination: { value: ['Control'], params: { id: info.row.original.id } },
                  tags: ['RMP'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => row.ownerName,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        cell: (info) => <GTFrameworkCell controls={info.row.original.frameworkControls} />,
        sortingFn: sortingFns.text,
      },
      {
        id: 'annual_cost',
        header: 'Annual Cost',
        accessorFn: (row) => row.annualCost || null,
        cell: ({ row }) => formatNumber(row.original.annualCost, { symbol: true }),
        sortingFn: sortingFns.alphanumeric,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'existingMaturity',
        header: 'Existing Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.existingMaturity),
      },
      {
        id: 'targetMaturity',
        header: 'Target Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.targetMaturity),
      },

      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: () => null,
        size: 1,
        maxSize: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.rmpMultiSelectModal}
      options={[
        { key: 'strategy', title: 'Strategy' },
        { key: 'name', title: 'Name' },
        { key: 'owner', title: 'Owner' },
        { key: 'framework_controls', title: 'Framework' },
        { key: 'annual_cost', title: 'Annual Cost' },
        { key: 'status', title: 'Status' },
        { key: 'existingMaturity', title: 'Existing Maturity' },
        { key: 'targetMaturity', title: 'Target Maturity' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, GTColumnSelectAnchorExported };
};
